import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import WaitlistModal from "../components/WaitlistModal";
import SovereignToolkit from "../components/SovereignToolkit";
import { Radio, ArrowRight, Zap, Flame } from "lucide-react";
import EigooMark from "../components/EigooMark";
import { BoardLink } from "../lib/boardLinks";

const NBA_SIGNALS = [
    { away: "LAL", home: "BOS", spread: "+4.5", total: "226.5", pick: "BOS -4.5", conf: 0.78, edge: "3.2%", status: "live" },
    { away: "DEN", home: "GSW", spread: "-2.0", total: "231.0", pick: "UNDER 231.0", conf: 0.71, edge: "2.5%", status: "tip 9pm ET" },
    { away: "MIL", home: "PHI", spread: "+1.5", total: "219.5", pick: "MIL +1.5", conf: 0.66, edge: "1.8%", status: "tip 7:30pm ET" },
    { away: "MIA", home: "NYK", spread: "+3.5", total: "214.5", pick: "OVER 214.5", conf: 0.62, edge: "1.4%", status: "tip 8pm ET" },
    { away: "DAL", home: "MEM", spread: "-5.5", total: "228.0", pick: "DAL -5.5", conf: 0.74, edge: "2.9%", status: "tomorrow" },
];

export default function SportsHome() {
    const { user } = useAuth();
    const [showWaitlist, setShowWaitlist] = useState(false);
    return (
        <div className="min-h-screen bg-background grain-overlay">
            {showWaitlist && (
                <WaitlistModal tenant="sports" label="Join the Signal waitlist" onClose={() => setShowWaitlist(false)} />
            )}
            <header className="relative z-10 max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 py-6">
                <div className="flex items-baseline gap-3">
                    <EigooMark size="lg" sub="Signal" testid="sports-mark" />
                </div>
                <div className="flex items-center gap-3">
                    {user ? (
                        <Link to="/app/dashboard" className="px-5 py-2 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] text-sm font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors" data-testid="sports-enter-portal">Enter Portal</Link>
                    ) : (
                        <>
                            <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">Sign In</Link>
                            <BoardLink data-testid="sports-board-book" className="px-5 py-2 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] text-sm font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors">Board the Book</BoardLink>
                        </>
                    )}
                </div>
            </header>

            <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-8 pb-24">
                <div className="fade-up max-w-3xl">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[hsl(40_50%_40%/0.4)] text-[10px] uppercase tracking-[0.3em] text-[hsl(45_60%_70%)] mb-6">
                        <Radio size={10} className="text-[hsl(45_85%_62%)]" /> Live · NBA Signal Feed
                    </div>
                    <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
                        Signals sharper
                        <span className="block gold-gradient-text mt-2">than the closing line.</span>
                    </h1>
                    <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
                        We model pace, rest, travel, and dealer positioning. Every pick ships with a confidence band
                        and the edge versus the sharp books — no guesswork, no hype.
                    </p>
                </div>

                {/* NBA Signal table */}
                <div className="mt-14 glass rounded-2xl p-6 fade-up" style={{ animationDelay: "0.15s" }}>
                    <div className="flex items-center justify-between mb-5">
                        <div>
                            <div className="font-heading text-2xl">Tonight's card</div>
                            <div className="text-xs text-muted-foreground">Models refresh hourly · sharp-line tracking <span className="text-[hsl(45_85%_68%)]">(demo feed)</span></div>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-[hsl(45_85%_68%)]">
                            <span className="w-2 h-2 rounded-full bg-[hsl(45_85%_62%)] animate-pulse" />
                            signal feed active
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {NBA_SIGNALS.map((g, i) => (
                            <div key={i} className="p-5 rounded-xl bg-[hsl(151_55%_13%)] border border-[hsl(40_40%_25%/0.18)]" data-testid={`game-${i}`}>
                                <div className="flex items-center justify-between">
                                    <div className="font-mono text-lg">{g.away} <span className="text-muted-foreground">@</span> {g.home}</div>
                                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${g.status === "live" ? "bg-[hsl(350_65%_35%)] text-[hsl(0_0%_98%)] pulse-warning" : "bg-[hsl(151_55%_18%)] text-muted-foreground"}`}>{g.status}</span>
                                </div>
                                <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Spread</div>
                                        <div className="font-mono mt-0.5">{g.spread}</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</div>
                                        <div className="font-mono mt-0.5">{g.total}</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Edge</div>
                                        <div className="font-mono mt-0.5 text-[hsl(45_85%_68%)]">{g.edge}</div>
                                    </div>
                                </div>
                                <div className="mt-4 pt-4 border-t border-[hsl(40_40%_30%/0.15)] flex items-center justify-between">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Pick</div>
                                        <div className="font-heading text-lg text-[hsl(45_85%_75%)]">{g.pick}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Conviction</div>
                                        <div className="flex items-center gap-1.5 mt-0.5">
                                            <Zap size={12} className="text-[hsl(45_85%_62%)]" />
                                            <span className="font-mono">{(g.conf * 100).toFixed(0)}%</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-6 p-4 rounded-lg border border-dashed border-[hsl(40_40%_30%/0.3)] text-xs text-muted-foreground">
                        <span className="text-[hsl(45_85%_68%)] font-semibold">Placeholder feed.</span> Paste your odds-feed credentials (The Odds API, SportsDataIO, or Bet365) and the models connect on their own.
                    </div>
                </div>

                <div className="mt-10 flex flex-wrap gap-4">
                    <button onClick={() => setShowWaitlist(true)} className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors" data-testid="sports-cta">
                        <Flame size={18} /> Request signal access <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                    </button>
                    {user && (
                        <Link to="/app/dashboard" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-[hsl(40_50%_35%/0.4)] text-foreground hover:bg-[hsl(151_55%_15%)] transition-colors">
                            Open portal
                        </Link>
                    )}
                </div>

                {/* Sovereign Toolkit · Sports affiliate signals (Director directive 2026.02) */}
                <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="md:col-span-2">
                        <h2 className="font-heading text-2xl mb-2">Sportsbook signal log</h2>
                        <p className="text-sm text-muted-foreground max-w-xl leading-relaxed">
                            The Signal platform converts on NBA / NFL / sportsbook acquisition flows. The Director's
                            scout has mapped the highest-yield programs to this rail.
                        </p>
                    </div>
                    <SovereignToolkit vessel="eigoo_sports" title="Sports Toolkit" />
                </div>
            </section>

            <footer className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 border-t border-[hsl(40_40%_30%/0.15)] text-xs text-muted-foreground flex flex-wrap items-center justify-between gap-4">
                <div>© EIGOO Signal · Sharper than closing.</div>
                <div className="font-mono">sports.eigooinc.net</div>
            </footer>
        </div>
    );
}
